import { SkeletonEvent } from '../types.js';
import { TAGS } from './tags.js';

const TRANSACTIONS = {
    init: {
        name: 'init',
        spans: {
            getAllMfs: 'getAllMfs',
            fetchingMetaData: 'fetchingMetaData',
            fetchingManifest: 'fetchingManifest',
            loadExternals: 'loadExternals',
            loadJsAssets: 'loadJsAssets',
            userInit: 'userInit',
        },
    },
    render: {
        name: 'render',
    },
};
class TransactionsManager {
    constructor(createTransaction, preloadedMFs) {
        this.transactionsByMf = {};
        this.createTransaction = createTransaction;
        this.preloadedMFs = preloadedMFs;
    }
    getTransactionByMf(mfName, transactionName) {
        const transactionsByMf = this.transactionsByMf[mfName];
        return transactionsByMf ? transactionsByMf[transactionName] : null;
    }
    startTransaction(mfName, transactionName, tags = {}) {
        var _a;
        const transaction = this.createTransaction(transactionName, mfName, tags);
        (_a = this.transactionsByMf)[mfName] || (_a[mfName] = {});
        this.transactionsByMf[mfName][transactionName] = transaction;
    }
    finishTransaction(mfName, transactionName) {
        const transaction = this.getTransactionByMf(mfName, transactionName);
        transaction?.finish();
    }
    startSpan(mfName, transactionName, spanName) {
        const transaction = this.getTransactionByMf(mfName, transactionName);
        if (transaction) {
            this.transactionsByMf[mfName][spanName] = transaction.startChild({ op: spanName, description: spanName });
        }
    }
    finishSpan(mfName, spanName) {
        if (!mfName || !spanName) {
            return;
        }
        const span = this.getTransactionByMf(mfName, spanName);
        span?.finish();
    }
    track(eventName, data = {}) {
        const { mfName, mfCanary = {}, version } = data;
        const { isInCanaryExperimentalGroup, isMfInCanary } = mfCanary;
        try {
            switch (eventName) {
                case SkeletonEvent.MFLoadStart: {
                    this.startTransaction(mfName, TRANSACTIONS.init.name, {
                        [TAGS.isInCanaryExperimentalGroup]: isInCanaryExperimentalGroup,
                        [TAGS.isMfInCanary]: isMfInCanary,
                        [TAGS.version]: version,
                    });
                    break;
                }
                case SkeletonEvent.MFLoadDone: {
                    this.finishTransaction(mfName, TRANSACTIONS.init.name);
                    break;
                }
                case SkeletonEvent.StartFetchingMetaData: {
                    this.startSpan(mfName, TRANSACTIONS.init.name, TRANSACTIONS.init.spans.fetchingMetaData);
                    break;
                }
                case SkeletonEvent.SuccessFetchingMetaData: {
                    this.finishSpan(mfName, TRANSACTIONS.init.spans.fetchingMetaData);
                    break;
                }
                case SkeletonEvent.StartFetchingManifest: {
                    this.startSpan(mfName, TRANSACTIONS.init.name, TRANSACTIONS.init.spans.fetchingManifest);
                    break;
                }
                case SkeletonEvent.SuccessFetchingManifest: {
                    this.finishSpan(mfName, TRANSACTIONS.init.spans.fetchingManifest);
                    break;
                }
                case SkeletonEvent.SharedDependenciesLoadStart: {
                    this.startSpan(mfName, TRANSACTIONS.init.name, TRANSACTIONS.init.spans.loadExternals);
                    break;
                }
                case SkeletonEvent.SharedDependenciesLoadEnd: {
                    this.finishSpan(mfName, TRANSACTIONS.init.spans.loadExternals);
                    break;
                }
                case SkeletonEvent.StartLoadingJsAssets: {
                    this.startSpan(mfName, TRANSACTIONS.init.name, TRANSACTIONS.init.spans.loadJsAssets);
                    break;
                }
                case SkeletonEvent.SuccessLoadingJsAssets: {
                    this.finishSpan(mfName, TRANSACTIONS.init.spans.loadJsAssets);
                    break;
                }
                case SkeletonEvent.MfUserInitStart: {
                    if (this.preloadedMFs.has(mfName)) {
                        this.startTransaction(mfName, TRANSACTIONS.init.name, {
                            [TAGS.isInCanaryExperimentalGroup]: isInCanaryExperimentalGroup,
                            [TAGS.isMfInCanary]: isMfInCanary,
                            [TAGS.version]: version,
                        });
                    }
                    this.startSpan(mfName, TRANSACTIONS.init.name, TRANSACTIONS.init.spans.userInit);
                    break;
                }
                case SkeletonEvent.MfUserInitSuccess: {
                    this.finishSpan(mfName, TRANSACTIONS.init.spans.userInit);
                    if (this.preloadedMFs.has(mfName)) {
                        this.finishTransaction(mfName, TRANSACTIONS.init.name);
                    }
                    break;
                }
                case SkeletonEvent.MfUserRenderStart: {
                    this.startTransaction(mfName, TRANSACTIONS.render.name);
                    const observer = new MutationObserver(mutationsList => {
                        // eslint-disable-next-line no-restricted-syntax
                        for (const mutation of mutationsList) {
                            if (mutation.type === 'childList') {
                                this.finishTransaction(mfName, TRANSACTIONS.render.name);
                                observer.disconnect();
                                break;
                            }
                        }
                    });
                    observer.observe(data.element, {
                        childList: true,
                    });
                    break;
                }
                default: {
                    break;
                }
            }
        }
        catch (e) {
            console.error(`Failed creating sentry transactions. event: ${eventName}. data:${JSON.stringify(data)}`);
        }
    }
}

export { TransactionsManager as default };
